import { useState } from 'react';
import Switch from '@/components/base/Switch';
import { Pill } from '@/components/base/Badges';
import { serviceTypeMeta, type LaundryPackage } from '@/mocks/packages';

interface PackageCardProps {
  pkg: LaundryPackage;
  onToggleActive: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onViewSubscribers: () => void;
}

export default function PackageCard({
  pkg,
  onToggleActive,
  onEdit,
  onDelete,
  onViewSubscribers,
}: PackageCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  const meta = serviceTypeMeta[pkg.serviceType];
  const discount = pkg.originalPrice - pkg.price;
  const discountPct =
    pkg.originalPrice > pkg.price
      ? Math.round((discount / pkg.originalPrice) * 100)
      : 0;
  const activeSubscribers = pkg.subscribers.filter((sub) => sub.status === 'active').length;

  return (
    <div
      className={[
        'flex flex-col rounded-card border border-background-200 bg-white p-5 shadow-card transition-opacity duration-300',
        pkg.active ? 'opacity-100' : 'opacity-50',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span
            className={[
              'flex h-12 w-12 shrink-0 items-center justify-center rounded-[10px]',
              pkg.active ? 'bg-secondary-100 text-secondary-600' : 'bg-background-100 text-foreground-400',
            ].join(' ')}
          >
            <i className={`${meta.icon} text-2xl`} aria-hidden="true"></i>
          </span>
          <div>
            <h3 className="font-semibold text-[16px] leading-tight text-foreground-950">{pkg.name}</h3>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <Pill tone="secondary" icon={meta.icon}>
                {meta.label}
              </Pill>
              {!pkg.active && <Pill tone="danger">معطّلة</Pill>}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Switch checked={pkg.active} onChange={onToggleActive} size="sm" />
          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-400 transition-colors hover:bg-background-100 hover:text-foreground-700"
              aria-label="خيارات الباقة"
            >
              <i className="ri-more-2-fill text-lg" aria-hidden="true"></i>
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} aria-hidden="true"></div>
                <div className="dc-fade-up absolute left-0 z-20 mt-1 w-[140px] rounded-card border border-background-200 bg-white p-1 shadow-soft">
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onEdit();
                    }}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-right text-[13px] text-foreground-700 transition-colors hover:bg-background-50"
                  >
                    <i className="ri-pencil-line text-base text-secondary-500" aria-hidden="true"></i>
                    تعديل
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onDelete();
                    }}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-right text-[13px] text-danger-600 transition-colors hover:bg-danger-50"
                  >
                    <i className="ri-delete-bin-line text-base" aria-hidden="true"></i>
                    حذف
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        <div className="rounded-lg bg-background-50 px-3 py-2.5 text-center">
          <span className="block text-[11px] text-foreground-400">عدد القطع</span>
          <span className="mt-0.5 block text-[15px] font-semibold text-foreground-900">{pkg.itemCount}</span>
        </div>
        <div className="rounded-lg bg-background-50 px-3 py-2.5 text-center">
          <span className="block text-[11px] text-foreground-400">مدة الصلاحية</span>
          <span className="mt-0.5 block text-[15px] font-semibold text-foreground-900">{pkg.validityDays} يوم</span>
        </div>
        <div className="rounded-lg bg-background-50 px-3 py-2.5 text-center">
          <span className="block text-[11px] text-foreground-400">السعر</span>
          <span className="mt-0.5 block text-[15px] font-semibold text-foreground-900">
            {pkg.price} <span className="text-[11px] font-normal text-foreground-400">ر.س</span>
          </span>
        </div>
      </div>

      {discountPct > 0 && (
        <div className="mt-3 flex items-center justify-between rounded-lg bg-accent-100 px-3 py-2">
          <span className="flex items-center gap-1.5 text-[13px] font-semibold text-accent-800">
            <i className="ri-percent-line text-base" aria-hidden="true"></i>
            وفّر {discountPct}٪
          </span>
          <span className="text-[13px] text-accent-700">
            <span className="line-through opacity-70">{pkg.originalPrice} ر.س</span>
          </span>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between gap-3 border-t border-background-100 pt-3">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
            <i className="ri-team-line text-base" aria-hidden="true"></i>
          </span>
          <div>
            <span className="block text-[13px] font-semibold text-foreground-900">
              {pkg.subscribers.length} مشترك
            </span>
            {activeSubscribers > 0 && (
              <span className="block text-[11px] text-foreground-400">
                {activeSubscribers} اشتراك نشط حالياً
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onViewSubscribers}
          className="inline-flex items-center gap-1 whitespace-nowrap rounded-btn border border-secondary-500 px-3 py-1.5 text-[13px] font-medium text-secondary-600 transition-colors hover:bg-secondary-50"
        >
          عرض المشتركين
          <i className="ri-arrow-left-line text-sm" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  );
}